import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { PlayerLoyalty } from '@sorare/core/src/atoms/icons/PlayerLoyalty';
import { VaultBoost } from '@sorare/core/src/atoms/icons/VaultBoost';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  HeadlineM,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { HELP } from '@sorare/core/src/constants/routes';

import sealingGenericAvatar from 'components/sealing/assets/sealingGenericAvatar.png';

import type { RarityVaultHowItWorksDialog_sealingConfig } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  padding: 0 var(--triple-unit) var(--triple-unit);

  & > * {
    max-width: calc(46 * var(--unit)); /* 368px */
  }
`;

const SealingGenericAvatar = styled.img.attrs({ src: sealingGenericAvatar })`
  width: 100px;
  height: 100px;
`;

const YellowText = styled.strong`
  color: var(--c-yellow-400);
`;

const ParagraphWithIcon = styled(Horizontal)`
  gap: var(--double-unit);
  & > :first-child {
    display: flex;
    align-items: center;
    justify-content: center;
    width: calc(6 * var(--unit));
    height: calc(6 * var(--unit));
  }
  & > :last-child {
    flex: 1;
  }
`;

const StyledLink = styled.a`
  text-decoration: underline;
`;

const Warning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--intermediate-unit);

  & > :first-child {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--half-unit);
  }
  & > :last-child {
    flex: 1;
  }
`;
type Props = {
  open: boolean;
  sealingConfig?: RarityVaultHowItWorksDialog_sealingConfig;
  loading: boolean;
  onClose: () => void;
};

export const RarityVaultHowItWorksDialog = ({
  open = true,
  sealingConfig,
  loading,
  onClose,
}: Props) => {
  return (
    <Dialog maxWidth="xs" fullWidth open={open} onClose={onClose}>
      {!loading && (
        <Root>
          <Vertical center gap={2} className="text-center">
            <SealingGenericAvatar />
            <Vertical>
              <HeadlineM color="var(--c-white)">
                <FormattedMessage
                  id="Collections.sealing.dialog.howitworks.title"
                  defaultMessage="Sealing Cards"
                />
              </HeadlineM>
              <LabelL color="var(--c-nd-600)">
                <FormattedMessage
                  id="Collections.sealing.dialog.howitworks.subtitle"
                  defaultMessage="Seal Cards to increase your Vault & Player Loyalty{br}
Each level unlocks perks like boosted rewards"
                  values={{ br: <br /> }}
                />
              </LabelL>
            </Vertical>
          </Vertical>
          <Vertical gap={1.5}>
            <ParagraphWithIcon>
              <div>
                <VaultBoost size={4} />
              </div>
              <Vertical gap={0}>
                <BodyM bold color="var(--c-nd-900)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.vault.title"
                    defaultMessage="Vaults"
                  />
                </BodyM>
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.vault.subtitle"
                    defaultMessage="Collect and Seal multiple cards to level up your Vault."
                  />
                </LabelS>
              </Vertical>
            </ParagraphWithIcon>
            <ParagraphWithIcon>
              <div>
                <PlayerLoyalty size={5} />
              </div>
              <Vertical gap={0}>
                <BodyM bold color="var(--c-nd-900)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.loyalty.title"
                    defaultMessage="Player Loyalty"
                  />
                </BodyM>
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.loyalty.subtitle"
                    defaultMessage="Seal cards from multiple seasons to increase your Loyalty Level."
                  />
                </LabelS>
              </Vertical>
            </ParagraphWithIcon>
            <ParagraphWithIcon>
              <div>
                <Gift size={3} />
              </div>
              <Vertical gap={0}>
                <BodyM bold color="var(--c-nd-900)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.perks.title"
                    defaultMessage="Unlock Perks"
                  />
                </BodyM>
                <LabelS color="var(--c-nd-600)">
                  <FormattedMessage
                    id="Collections.sealing.dialog.howitworks.perks.subtitle"
                    defaultMessage="Reach milestones to earn Perks like boosted rewards"
                  />
                </LabelS>
              </Vertical>
            </ParagraphWithIcon>
          </Vertical>
          <Warning>
            <LabelM color="var(--c-yellow-400)">
              <FontAwesomeIcon icon={faInfoCircle} />
            </LabelM>
            <Vertical gap={1.5}>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  id="Collections.sealing.dialog.howitworks.info"
                  defaultMessage="Sealed cards cannot be <strong>bought, sold, or used in lineups</strong>. You can Unseal up to <strong>{unsealsPerPeriod, plural, one {# card} other {# cards}} per day per scarcity</strong>. You must wait {unsealCooldownDays, plural, one {# day} other {# days}} to unseal a card. Vaults & Loyalty are <strong>grouped by Scarcity</strong>."
                  values={{
                    br: <br />,
                    strong: (chunks: ReactNode[]) => (
                      <YellowText>{chunks}</YellowText>
                    ),
                    unsealCooldownDays: sealingConfig?.unsealCooldownDays,
                    unsealsPerPeriod: sealingConfig?.unsealsPerPeriod,
                  }}
                />
              </LabelM>
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  id="Collections.sealing.dialog.howitworks.helpCenterLink"
                  defaultMessage="Learn more in our <link>Help Center</link>."
                  values={{
                    link: (chunks: ReactNode[]) => (
                      <StyledLink href={HELP} target="_blank">
                        {chunks}
                      </StyledLink>
                    ),
                  }}
                />
              </LabelM>
            </Vertical>
          </Warning>
        </Root>
      )}
    </Dialog>
  );
};

RarityVaultHowItWorksDialog.fragments = {
  sealingConfig: gql`
    fragment RarityVaultHowItWorksDialog_sealingConfig on SealingConfig {
      unsealCooldownDays
      unsealsPerPeriod
    }
  ` as TypedDocumentNode<RarityVaultHowItWorksDialog_sealingConfig>,
};
